package dev.lingvo.shared.screenshots.text

import dev.lingvo.shared.screenshots.render.ResolvedFont
import dev.lingvo.shared.screenshots.render.TextMeasurer
import kotlin.math.max

internal data class MeasuredPiece(val text: String, val width: Double, val breakAfter: Brk)
internal data class FitLine(val text: String, val width: Double, val hyphenated: Boolean)
internal data class LineRun(val lines: List<FitLine>, val tooWide: Boolean)

/** Reference widths are shared across both discretionary-hyphen passes and every search step. */
internal class LineLayout(
    private val req: FitRequest,
    private val normal: NormalisedText,
    private val measurer: TextMeasurer,
    segments: List<Segment>,
) {
    private val reference = font(100.0)
    private val tracking = normal.letterSpacingEm * 100.0
    private val space = measurer.width(" ", reference, tracking)
    private val hyphen = measurer.width("-", reference, tracking)
    private val pieces = segments.map { segment ->
        val text = segment.text.replace("\u00ad", "").replace("\u200b", "").replace("\n", "")
        MeasuredPiece(text, measurer.width(text, reference, tracking), segment.breakAfter)
    }

    fun font(size: Double): ResolvedFont = ResolvedFont(cssFont(req.font, size, req.weight, req.italic), size,
        req.font.families, req.weight, req.italic)

    private fun atoms(hyphens: Boolean): List<MeasuredPiece> = buildList {
        var text = ""
        var width = 0.0
        for (piece in pieces) {
            if (text.isNotEmpty() && piece.text.isNotEmpty()) width += tracking
            text += piece.text
            width += piece.width
            if (piece.breakAfter != Brk.NONE && (piece.breakAfter != Brk.SOFT_HYPHEN || hyphens)) {
                add(MeasuredPiece(text, width, piece.breakAfter))
                text = ""
                width = 0.0
            }
        }
        if (text.isNotEmpty()) add(MeasuredPiece(text, width, Brk.NONE))
    }

    fun at(size: Double, width: Double, hyphens: Boolean, real: Boolean = false): LineRun {
        val atoms = atoms(hyphens)
        val lines = mutableListOf<FitLine>()
        var tooWide = false
        var start = 0
        while (start < atoms.size) {
            var text = ""
            var fullWidth = 0.0
            var lastEnd = -1
            var lastLine: FitLine? = null
            var i = start
            while (i < atoms.size) {
                val atom = atoms[i]
                if (text.isNotEmpty() && atom.text.isNotEmpty()) fullWidth += tracking
                text += atom.text
                fullWidth += atom.width
                val hard = atom.breakAfter == Brk.HARD
                val terminal = i == atoms.lastIndex || hard
                val suffix = if (atom.breakAfter == Brk.SOFT_HYPHEN && !terminal) "-" else ""
                val clean = text.trimEnd(' ')
                val trailing = text.length - clean.length
                val trimWidth = fullWidth - trailing * (space + tracking) + if (clean.isEmpty() && trailing > 0) tracking else 0.0
                val refWidth = trimWidth + if (suffix.isNotEmpty()) hyphen + if (clean.isNotEmpty()) tracking else 0.0 else 0.0
                val candidate = FitLine(clean + suffix,
                    if (real) measurer.width(clean + suffix, font(size), normal.letterSpacingEm * size) else max(0.0, refWidth * size / 100.0),
                    suffix.isNotEmpty())
                val bodyWidth = if (suffix.isEmpty()) candidate.width else if (real) {
                    measurer.width(clean, font(size), normal.letterSpacingEm * size)
                } else max(0.0, trimWidth * size / 100.0)
                if (candidate.width <= width + 1e-7) {
                    lastEnd = i
                    lastLine = candidate
                }
                if (bodyWidth > width + 1e-7 && lastEnd >= start) break
                // A non-fitting first atom is indivisible at this size. Keep it for honest overflow geometry.
                if (hard || terminal || lastEnd < start && bodyWidth > width + 1e-7) {
                    lastEnd = i
                    lastLine = candidate
                    break
                }
                i++
            }
            val line = requireNotNull(lastLine)
            lines.add(line)
            tooWide = tooWide || line.width > width + 1e-7
            start = lastEnd + 1
        }
        return LineRun(lines, tooWide)
    }
}
